import { hoistStatic } from '@rehaje/shared'
import { forwardRef, memo } from 'react'

import { ReactForward, ReactMemo } from './constants'
import { useObserver } from './hooks/useObserver'

import type {
  ForwardFunction,
  IExoticFC,
  IObserverOptions,
  IObserverProps,
  MemoComponent,
  MemoForwardComponent,
  PropsWithRef,
  RefType
} from './types'
import type { Merge } from '@rehaje/shared'
import type { FC, PropsWithChildren } from 'react'

export function observer<P, R extends RefType<P> = RefType<P>>(
  component: ForwardFunction<R, PropsWithRef<P, R>>,
  options: Merge<IObserverOptions, { forwardRef: true }>
): MemoForwardComponent<PropsWithRef<P, R>>

export function observer<P>(
  component: FC<PropsWithChildren<P>>,
  options?: Merge<IObserverOptions, { forwardRef?: boolean }>
): MemoComponent<FC<PropsWithChildren<P>>>
export function observer(
  component: IExoticFC,
  options: IObserverOptions = {}
): IExoticFC {
  let observer: IExoticFC, render: IExoticFC

  if (
    (typeof component !== 'function' &&
      (typeof component !== 'object' || component === null)) ||
    (render = getRender(component)) === undefined
  ) {
    return component
  }

  options.memo ??= true
  options.forwardRef ??= component.$$typeof === ReactForward
  options.displayName ??= component.displayName ?? component.name ?? 'Component'

  observer = (props, ref) => useObserver(render, props, ref, options.scheduler)
  if (options.forwardRef) observer = forwardRef(observer)
  if (options.memo) observer = memo(observer, options.equals)

  hoistStatic(observer, component)
  observer.displayName = options.displayName
  observer.$$render = render

  return observer
}
function getRender(component?: any): any {
  if (typeof component === 'function') return component
  if (typeof component === 'object' && component !== null) {
    if (component.$$render !== undefined) return
    if (component.$$typeof === ReactMemo) return getRender(component.type)
    if (component.$$typeof === ReactForward) return getRender(component.render)
  }
}

export const Observer: FC<IObserverProps> = memo(({ children }) => {
  return typeof children === 'function' ? useObserver(children) : children
})
Observer.displayName = 'Observer'
